import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CalendarClock, Ship, User, Calendar, ArrowRight, Loader2, AlertCircle,
  Plus, Search, Filter, X, Check, Trash2, Edit2, Eye, Sparkles,
  AlertTriangle, Clock, CheckCircle, Users, RefreshCw, Wand2, ChevronDown,
  FileText, BarChart3, TrendingUp, Activity, Zap, ListTodo, CalendarDays,
  Bot, ChevronRight, Target, UserPlus, Download, Upload, Settings, Play
} from 'lucide-react';
import { Header, Modal } from '../components';
import { crewApi } from '../services/api';
import type { 
  ReliefPlan, ReliefPlanFormData, Vessel, ReliefStatistics,
  AIBulkSuggestResponse, AIBulkSuggestion, AIReliefScheduleResponse, ReliefScheduleEntry,
  AIOptimizeTimingResponse
} from '../types';
import clsx from 'clsx';
import toast from 'react-hot-toast';

// ========================
// Constants & Helpers
// ========================

type ViewTab = 'plans' | 'schedule' | 'ai-assistant' | 'conflicts' | 'analytics';

const statusStyles = {
  confirmed: 'bg-green-100 text-green-800 border-green-200',
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  overdue: 'bg-red-100 text-red-800 border-red-200',
  cancelled: 'bg-gray-100 text-gray-600 border-gray-200',
};

const urgencyStyles = {
  critical: 'bg-red-100 text-red-800 border-red-200',
  high: 'bg-orange-100 text-orange-800 border-orange-200',
  medium: 'bg-amber-100 text-amber-800 border-amber-200',
  low: 'bg-green-100 text-green-800 border-green-200',
};

const rankOptions = [
  { value: '', label: 'All Ranks' },
  { value: 'MSTR', label: 'Master' },
  { value: 'COFF', label: 'Chief Officer' },
  { value: 'CENG', label: 'Chief Engineer' },
  { value: '2ENG', label: 'Second Engineer' },
  { value: '3ENG', label: '3rd Engineer' },
  { value: '4ENG', label: '4th Engineer' },
];

const statusOptions = [
  { value: '', label: 'All Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'overdue', label: 'Overdue' },
];

const getRankLabel = (rank: string): string => {
  const rankLabels: Record<string, string> = {
    'MSTR': 'Master',
    'COFF': 'Chief Officer',
    'CENG': 'Chief Engineer',
    '2ENG': 'Second Engineer',
    '3ENG': '3rd Engineer',
    '4ENG': '4th Engineer',
  };
  return rankLabels[rank] || rank;
};

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return 'N/A';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

const isOverdue = (dueDate?: string): boolean => {
  if (!dueDate) return false;
  return new Date(dueDate) < new Date();
};

// ========================
// Statistics Dashboard Component
// ========================

interface StatisticsDashboardProps {
  stats: ReliefStatistics | null;
  isLoading: boolean;
}

function StatisticsDashboard({ stats, isLoading }: StatisticsDashboardProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
            <div className="h-8 bg-gray-200 rounded w-12"></div>
          </div>
        ))}
      </div>
    );
  }

  if (!stats) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6"
    >
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-1">
          <FileText className="w-4 h-4 text-gray-400" />
          <span className="text-sm text-gray-500">Total Plans</span>
        </div>
        <p className="text-2xl font-bold text-gray-900">{stats.summary.total}</p>
      </div>
      
      <div className="bg-white rounded-xl border-l-4 border-l-amber-500 border border-gray-200 p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-1">
          <Clock className="w-4 h-4 text-amber-500" />
          <span className="text-sm text-gray-500">Pending</span>
        </div>
        <p className="text-2xl font-bold text-amber-600">{stats.summary.pending}</p>
      </div>
      
      <div className="bg-white rounded-xl border-l-4 border-l-green-500 border border-gray-200 p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-1">
          <CheckCircle className="w-4 h-4 text-green-500" />
          <span className="text-sm text-gray-500">Confirmed</span>
        </div>
        <p className="text-2xl font-bold text-green-600">{stats.summary.confirmed}</p>
      </div>
      
      <div className="bg-white rounded-xl border-l-4 border-l-red-500 border border-gray-200 p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-1">
          <AlertTriangle className="w-4 h-4 text-red-500" />
          <span className="text-sm text-gray-500">Overdue</span>
        </div>
        <p className="text-2xl font-bold text-red-600">{stats.summary.overdue}</p>
      </div>
      
      <div className="bg-white rounded-xl border-l-4 border-l-purple-500 border border-gray-200 p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-1">
          <Users className="w-4 h-4 text-purple-500" />
          <span className="text-sm text-gray-500">Unassigned</span>
        </div>
        <p className="text-2xl font-bold text-purple-600">{stats.summary.unassigned}</p>
      </div>
      
      <div className="bg-white rounded-xl border-l-4 border-l-blue-500 border border-gray-200 p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-1">
          <TrendingUp className="w-4 h-4 text-blue-500" />
          <span className="text-sm text-gray-500">Completion</span>
        </div>
        <p className="text-2xl font-bold text-blue-600">{stats.summary.completion_rate}%</p>
      </div>
    </motion.div>
  );
}

// ========================
// Relief Plan Form Component
// ========================

interface ReliefPlanFormProps {
  initialData?: Partial<ReliefPlanFormData>;
  onSubmit: (data: ReliefPlanFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  vessels?: Vessel[];
}

function ReliefPlanForm({ initialData, onSubmit, onCancel, isLoading, vessels = [] }: ReliefPlanFormProps) {
  const [formData, setFormData] = useState<ReliefPlanFormData>({
    vessel_name: initialData?.vessel_name || '',
    rank: initialData?.rank || '',
    outgoing_cid: initialData?.outgoing_cid || '',
    outgoing_name: initialData?.outgoing_name || '',
    incoming_cid: initialData?.incoming_cid || '',
    incoming_name: initialData?.incoming_name || '',
    due_date: initialData?.due_date || '',
    status: initialData?.status || 'pending',
    notes: initialData?.notes || '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.vessel_name || !formData.rank || !formData.due_date) {
      toast.error('Please fill in all required fields');
      return;
    }
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Vessel Name *</label>
          <input
            type="text"
            value={formData.vessel_name}
            onChange={(e) => setFormData({ ...formData, vessel_name: e.target.value })}
            placeholder="Enter vessel name"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            list="vessel-suggestions"
          />
          <datalist id="vessel-suggestions">
            {vessels.map((v) => (
              <option key={v.id} value={v.name} />
            ))}
          </datalist>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Rank *</label>
          <select
            value={formData.rank}
            onChange={(e) => setFormData({ ...formData, rank: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select Rank</option>
            {rankOptions.filter(r => r.value).map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Due Date *</label>
          <input
            type="date"
            value={formData.due_date}
            onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
          </select>
        </div>
      </div>

      <div className="border-t pt-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
          <User className="w-4 h-4 text-red-500" />
          Outgoing Crew (Signing Off)
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">CID</label>
            <input
              type="text"
              value={formData.outgoing_cid}
              onChange={(e) => setFormData({ ...formData, outgoing_cid: e.target.value })}
              placeholder="e.g., 710101"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Name</label>
            <input
              type="text"
              value={formData.outgoing_name}
              onChange={(e) => setFormData({ ...formData, outgoing_name: e.target.value })}
              placeholder="Current crew member name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      <div className="border-t pt-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
          <User className="w-4 h-4 text-green-500" />
          Incoming Crew (Signing On)
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">CID</label>
            <input
              type="text"
              value={formData.incoming_cid}
              onChange={(e) => setFormData({ ...formData, incoming_cid: e.target.value })}
              placeholder="e.g., 710102"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Name</label>
            <input
              type="text"
              value={formData.incoming_name}
              onChange={(e) => setFormData({ ...formData, incoming_name: e.target.value })}
              placeholder="Replacement crew name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Additional notes about this relief plan..."
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
        >
          {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          {initialData ? 'Update Relief Plan' : 'Create Relief Plan'}
        </button>
      </div>
    </form>
  );
}

// ========================
// AI Crew Suggestion Panel
// ========================

interface CrewSuggestion {
  cid: string;
  name: string;
  surname?: string;
  rank: string;
  nationality?: string;
  experience_months?: number;
  contracts?: number;
  crew_type?: string;
  readiness?: string;
  match_score: number;
  match_reasons: string[];
}

interface AISuggestionPanelProps {
  planId: number;
  rank: string;
  vesselName: string;
  onAssign: (crewCid: string) => void;
  isAssigning?: boolean;
}

function AISuggestionPanel({ planId, rank, vesselName, onAssign, isAssigning }: AISuggestionPanelProps) {
  const [suggestions, setSuggestions] = useState<CrewSuggestion[]>([]);
  const [aiReasoning, setAiReasoning] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSuggestions = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await crewApi.suggestReliefCrew(planId, 10);
      setSuggestions(result.suggestions);
      setAiReasoning(result.ai_reasoning);
    } catch (err) {
      setError('Failed to load AI suggestions');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSuggestions();
  }, [planId]);

  return (
    <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl border border-purple-200 p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-purple-600" />
          <h4 className="font-semibold text-gray-900">AI Crew Suggestions</h4>
        </div>
        <button
          onClick={loadSuggestions}
          disabled={isLoading}
          className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1"
        >
          <RefreshCw className={clsx("w-4 h-4", isLoading && "animate-spin")} />
          Refresh
        </button>
      </div>

      {aiReasoning && (
        <p className="text-sm text-gray-600 mb-4 bg-white/50 p-2 rounded-lg">{aiReasoning}</p>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
        </div>
      ) : error ? (
        <div className="text-center py-8 text-red-600">
          <AlertCircle className="w-8 h-8 mx-auto mb-2" />
          <p>{error}</p>
        </div>
      ) : suggestions.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Users className="w-8 h-8 mx-auto mb-2 text-gray-400" />
          <p>No matching candidates found</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {suggestions.map((crew, index) => (
            <div
              key={crew.cid}
              className="bg-white rounded-lg border border-gray-200 p-3 hover:border-purple-300 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-gray-900">
                      {crew.name} {crew.surname}
                    </span>
                    <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                      {crew.cid}
                    </span>
                    {index === 0 && (
                      <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full font-medium">
                        Best Match
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                    {crew.nationality && <span>📍 {crew.nationality}</span>}
                    {crew.experience_months && (
                      <span>⏱ {Math.floor(crew.experience_months / 12)}y {crew.experience_months % 12}m exp</span>
                    )}
                    {crew.contracts && <span>📋 {crew.contracts} contracts</span>}
                  </div>
                  {crew.match_reasons && crew.match_reasons.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {crew.match_reasons.slice(0, 3).map((reason, i) => (
                        <span key={i} className="text-xs px-2 py-0.5 bg-green-50 text-green-700 rounded">
                          {reason}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="text-right ml-4">
                  <div className={clsx(
                    "text-lg font-bold",
                    crew.match_score >= 90 ? "text-green-600" :
                    crew.match_score >= 75 ? "text-blue-600" :
                    "text-amber-600"
                  )}>
                    {crew.match_score}%
                  </div>
                  <button
                    onClick={() => onAssign(crew.cid)}
                    disabled={isAssigning}
                    className="mt-2 text-xs px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                  >
                    {isAssigning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                    Assign
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ========================
// Relief Schedule View Component
// ========================

interface ReliefScheduleViewProps {
  schedule: AIReliefScheduleResponse | null;
  isLoading: boolean;
  onRefresh: () => void;
  onCreatePlan: (crewCid: string) => void;
}

function ReliefScheduleView({ schedule, isLoading, onRefresh, onCreatePlan }: ReliefScheduleViewProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (!schedule) {
    return (
      <div className="text-center py-12 text-gray-500">
        <CalendarDays className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <p>No schedule data available</p>
        <button onClick={onRefresh} className="mt-4 text-blue-600 hover:text-blue-700">
          Load Schedule
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className={clsx(
          "rounded-lg p-4 border",
          schedule.summary.critical > 0 ? "bg-red-50 border-red-200" : "bg-gray-50 border-gray-200"
        )}>
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className={clsx(
              "w-4 h-4",
              schedule.summary.critical > 0 ? "text-red-600" : "text-gray-400"
            )} />
            <span className="text-sm text-gray-600">Critical (≤7 days)</span>
          </div>
          <p className={clsx(
            "text-2xl font-bold",
            schedule.summary.critical > 0 ? "text-red-600" : "text-gray-900"
          )}>
            {schedule.summary.critical}
          </p>
        </div>
        
        <div className={clsx(
          "rounded-lg p-4 border",
          schedule.summary.high > 0 ? "bg-orange-50 border-orange-200" : "bg-gray-50 border-gray-200"
        )}>
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-orange-500" />
            <span className="text-sm text-gray-600">High (≤14 days)</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{schedule.summary.high}</p>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="flex items-center gap-2 mb-1">
            <FileText className="w-4 h-4 text-purple-500" />
            <span className="text-sm text-gray-600">No Relief Plan</span>
          </div>
          <p className="text-2xl font-bold text-purple-600">{schedule.summary.without_relief_plan}</p>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-4 h-4 text-blue-500" />
            <span className="text-sm text-gray-600">No Replacement</span>
          </div>
          <p className="text-2xl font-bold text-blue-600">{schedule.summary.without_replacement}</p>
        </div>
      </div>

      {/* AI Recommendations */}
      {schedule.ai_recommendations.filter(Boolean).length > 0 && (
        <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl border border-purple-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Bot className="w-5 h-5 text-purple-600" />
            <h4 className="font-semibold text-gray-900">AI Recommendations</h4>
          </div>
          <div className="space-y-2">
            {schedule.ai_recommendations.filter(Boolean).map((rec, i) => (
              <p key={i} className="text-sm text-gray-700">{rec}</p>
            ))}
          </div>
        </div>
      )}

      {/* Schedule Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Crew / Rank</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Vessel</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Due Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Urgency</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Relief Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Candidates</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {schedule.schedule.map((entry, index) => (
                <motion.tr
                  key={entry.crew_cid}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.02 }}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-gray-900">{entry.crew_name}</p>
                      <p className="text-sm text-gray-500">{getRankLabel(entry.rank)} • {entry.crew_cid}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{entry.vessel || 'N/A'}</td>
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-sm text-gray-900">{formatDate(entry.due_date)}</p>
                      {entry.days_until_due !== undefined && (
                        <p className={clsx(
                          "text-xs",
                          entry.days_until_due <= 0 ? "text-red-600 font-medium" :
                          entry.days_until_due <= 7 ? "text-orange-600" : "text-gray-500"
                        )}>
                          {entry.days_until_due <= 0 ? `${Math.abs(entry.days_until_due)} days overdue` : `${entry.days_until_due} days`}
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={clsx(
                      "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border",
                      urgencyStyles[entry.urgency]
                    )}>
                      {entry.urgency}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {entry.has_relief_plan ? (
                      <div className="flex items-center gap-1">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span className={clsx(
                          "text-sm",
                          entry.replacement_assigned ? "text-green-600" : "text-amber-600"
                        )}>
                          {entry.replacement_assigned ? 'Assigned' : 'Pending'}
                        </span>
                      </div>
                    ) : (
                      <span className="text-sm text-red-600 flex items-center gap-1">
                        <X className="w-4 h-4" />
                        No Plan
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">{entry.available_candidates} available</span>
                      {entry.top_candidate && (
                        <span className="text-xs text-purple-600">
                          Top: {entry.top_candidate.name}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {!entry.has_relief_plan && (
                      <button
                        onClick={() => onCreatePlan(entry.crew_cid)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-purple-600 font-medium rounded-lg hover:bg-purple-50 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        Create Plan
                      </button>
                    )}
                    {entry.has_relief_plan && entry.relief_plan_id && (
                      <span className="text-xs text-gray-400">Plan #{entry.relief_plan_id}</span>
                    )}
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ========================
// AI Assistant Panel Component
// ========================

interface AIAssistantPanelProps {
  onRefreshPlans: () => void;
}

function AIAssistantPanel({ onRefreshPlans }: AIAssistantPanelProps) {
  const [bulkSuggestions, setBulkSuggestions] = useState<AIBulkSuggestResponse | null>(null);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [daysAhead, setDaysAhead] = useState(30);
  const [threshold, setThreshold] = useState(85);
  const [selectedPlanIds, setSelectedPlanIds] = useState<number[]>([]);

  const loadBulkSuggestions = async () => {
    setIsLoadingSuggestions(true);
    try {
      const result = await crewApi.aiBulkSuggestReliefPlans({
        days_ahead: daysAhead,
        auto_assign_threshold: threshold,
      });
      setBulkSuggestions(result);
      // Pre-select all auto-assignable plans
      setSelectedPlanIds(result.auto_assignable.map(s => s.plan_id));
    } catch (error) {
      toast.error('Failed to load AI suggestions');
      console.error(error);
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  const executeBulkAssignment = async (autoOnly: boolean = false) => {
    if (selectedPlanIds.length === 0) {
      toast.error('No plans selected');
      return;
    }

    setIsExecuting(true);
    try {
      const result = await crewApi.aiExecuteBulkAssignments({
        plan_ids: selectedPlanIds,
        auto_only: autoOnly,
        threshold: threshold,
      });
      
      toast.success(result.ai_summary);
      onRefreshPlans();
      loadBulkSuggestions(); // Refresh suggestions
    } catch (error) {
      toast.error('Failed to execute bulk assignment');
      console.error(error);
    } finally {
      setIsExecuting(false);
    }
  };

  const togglePlanSelection = (planId: number) => {
    setSelectedPlanIds(prev => 
      prev.includes(planId) 
        ? prev.filter(id => id !== planId)
        : [...prev, planId]
    );
  };

  const selectAll = () => {
    if (bulkSuggestions) {
      setSelectedPlanIds(bulkSuggestions.suggestions.filter(s => s.suggested_crew).map(s => s.plan_id));
    }
  };

  const selectAutoOnly = () => {
    if (bulkSuggestions) {
      setSelectedPlanIds(bulkSuggestions.auto_assignable.map(s => s.plan_id));
    }
  };

  return (
    <div className="space-y-6">
      {/* Configuration */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
            <Bot className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">AI Bulk Assignment</h3>
            <p className="text-sm text-gray-500">Automatically assign crew to multiple relief plans</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Days Ahead</label>
            <select
              value={daysAhead}
              onChange={(e) => setDaysAhead(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            >
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
              <option value={60}>60 days</option>
              <option value={90}>90 days</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Auto-Assign Threshold</label>
            <select
              value={threshold}
              onChange={(e) => setThreshold(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            >
              <option value={70}>70% match</option>
              <option value={80}>80% match</option>
              <option value={85}>85% match</option>
              <option value={90}>90% match</option>
              <option value={95}>95% match</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={loadBulkSuggestions}
              disabled={isLoadingSuggestions}
              className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoadingSuggestions ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
              Analyze Plans
            </button>
          </div>
        </div>
      </div>

      {/* Results */}
      {bulkSuggestions && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          {/* Summary */}
          <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl border border-purple-200 p-4">
            <p className="text-sm text-gray-700 mb-3">{bulkSuggestions.ai_summary}</p>
            <div className="flex flex-wrap gap-4 text-sm">
              <span className="flex items-center gap-1">
                <FileText className="w-4 h-4 text-gray-400" />
                {bulkSuggestions.total_unassigned} unassigned
              </span>
              <span className="flex items-center gap-1 text-green-600">
                <Check className="w-4 h-4" />
                {bulkSuggestions.suggestions_generated} matches found
              </span>
              <span className="flex items-center gap-1 text-purple-600">
                <Zap className="w-4 h-4" />
                {bulkSuggestions.auto_assignable_count} auto-assignable
              </span>
            </div>
          </div>

          {/* Selection Actions */}
          <div className="flex items-center justify-between bg-white rounded-lg border border-gray-200 p-3">
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">
                {selectedPlanIds.length} of {bulkSuggestions.suggestions.length} selected
              </span>
              <button
                onClick={selectAll}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                Select All
              </button>
              <button
                onClick={selectAutoOnly}
                className="text-sm text-purple-600 hover:text-purple-700"
              >
                Select Auto-Only
              </button>
              <button
                onClick={() => setSelectedPlanIds([])}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Clear
              </button>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => executeBulkAssignment(true)}
                disabled={isExecuting || selectedPlanIds.length === 0}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2 text-sm"
              >
                {isExecuting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                Auto-Assign High Confidence
              </button>
              <button
                onClick={() => executeBulkAssignment(false)}
                disabled={isExecuting || selectedPlanIds.length === 0}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 text-sm"
              >
                {isExecuting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                Assign Selected
              </button>
            </div>
          </div>

          {/* Suggestions List */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="divide-y divide-gray-100">
              {bulkSuggestions.suggestions.map((suggestion) => (
                <div
                  key={suggestion.plan_id}
                  className={clsx(
                    "p-4 transition-colors",
                    selectedPlanIds.includes(suggestion.plan_id) ? "bg-purple-50" : "hover:bg-gray-50"
                  )}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selectedPlanIds.includes(suggestion.plan_id)}
                      onChange={() => togglePlanSelection(suggestion.plan_id)}
                      disabled={!suggestion.suggested_crew}
                      className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <span className="font-medium text-gray-900">{suggestion.vessel_name}</span>
                          <span className="mx-2 text-gray-400">•</span>
                          <span className="text-gray-600">{getRankLabel(suggestion.rank)}</span>
                          {suggestion.outgoing_name && (
                            <>
                              <span className="mx-2 text-gray-400">•</span>
                              <span className="text-red-600 text-sm">Outgoing: {suggestion.outgoing_name}</span>
                            </>
                          )}
                        </div>
                        <span className="text-sm text-gray-500">Due: {formatDate(suggestion.due_date)}</span>
                      </div>
                      
                      {suggestion.suggested_crew ? (
                        <div className="flex items-center justify-between bg-green-50 rounded-lg p-3">
                          <div>
                            <span className="text-sm font-medium text-gray-900">
                              {suggestion.suggested_crew.name}
                            </span>
                            <span className="mx-2 text-gray-400">•</span>
                            <span className="text-sm text-gray-500">{suggestion.suggested_crew.cid}</span>
                            {suggestion.suggested_crew.nationality && (
                              <span className="ml-2 text-sm text-gray-500">
                                📍 {suggestion.suggested_crew.nationality}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={clsx(
                              "text-lg font-bold",
                              suggestion.suggested_crew.match_score >= 90 ? "text-green-600" :
                              suggestion.suggested_crew.match_score >= 75 ? "text-blue-600" : "text-amber-600"
                            )}>
                              {suggestion.suggested_crew.match_score}%
                            </span>
                            {suggestion.auto_assignable && (
                              <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-full">
                                Auto-assignable
                              </span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-amber-600 bg-amber-50 rounded-lg p-3">
                          <AlertTriangle className="w-4 h-4" />
                          <span className="text-sm">{suggestion.warning || 'No suitable candidates found'}</span>
                        </div>
                      )}
                      
                      {suggestion.alternatives_count > 0 && (
                        <p className="mt-2 text-xs text-gray-500">
                          {suggestion.alternatives_count} alternative candidates available
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {!bulkSuggestions && !isLoadingSuggestions && (
        <div className="text-center py-12 text-gray-500">
          <Bot className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">AI Assistant Ready</h3>
          <p className="mb-4">Configure parameters and click "Analyze Plans" to get AI suggestions</p>
        </div>
      )}
    </div>
  );
}

// ========================
// AI Conflict Detection Panel
// ========================

interface ConflictsPanelProps {
  onRefreshPlans: () => void;
}

function ConflictsPanel({ onRefreshPlans }: ConflictsPanelProps) {
  const [conflicts, setConflicts] = useState<{
    total_plans_checked: number;
    conflicts: Array<{
      type: string;
      severity: string;
      message: string;
      plan_id?: number;
      vessel?: string;
      rank?: string;
      due_date?: string;
      days_until_due?: number;
      crew_cid?: string;
      crew_name?: string;
    }>;
    warnings: Array<{
      type: string;
      severity: string;
      message: string;
      plan_id?: number;
      vessel?: string;
    }>;
    conflict_count: number;
    warning_count: number;
    ai_summary: string;
    is_healthy: boolean;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [daysAhead, setDaysAhead] = useState(90);

  const loadConflicts = async () => {
    setIsLoading(true);
    try {
      const result = await crewApi.aiDetectConflicts(daysAhead);
      setConflicts(result);
    } catch (error) {
      toast.error('Failed to detect conflicts');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadConflicts();
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">AI Conflict Detection</h3>
              <p className="text-sm text-gray-500">Detect scheduling conflicts and issues</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={daysAhead}
              onChange={(e) => setDaysAhead(Number(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
            >
              <option value={30}>30 days ahead</option>
              <option value={60}>60 days ahead</option>
              <option value={90}>90 days ahead</option>
              <option value={180}>180 days ahead</option>
            </select>
            <button
              onClick={loadConflicts}
              disabled={isLoading}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Scan for Conflicts
            </button>
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-10 h-10 text-red-600 animate-spin" />
        </div>
      )}

      {conflicts && !isLoading && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className={clsx(
              "rounded-xl p-4 border",
              conflicts.is_healthy ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"
            )}>
              <div className="flex items-center gap-2 mb-1">
                {conflicts.is_healthy ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                )}
                <span className="text-sm text-gray-600">Health Status</span>
              </div>
              <p className={clsx(
                "text-xl font-bold",
                conflicts.is_healthy ? "text-green-600" : "text-red-600"
              )}>
                {conflicts.is_healthy ? "Healthy" : "Issues Found"}
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-4 border border-gray-200">
              <div className="flex items-center gap-2 mb-1">
                <FileText className="w-5 h-5 text-gray-400" />
                <span className="text-sm text-gray-600">Plans Checked</span>
              </div>
              <p className="text-xl font-bold text-gray-900">{conflicts.total_plans_checked}</p>
            </div>
            
            <div className={clsx(
              "rounded-xl p-4 border",
              conflicts.conflict_count > 0 ? "bg-red-50 border-red-200" : "bg-gray-50 border-gray-200"
            )}>
              <div className="flex items-center gap-2 mb-1">
                <X className="w-5 h-5 text-red-500" />
                <span className="text-sm text-gray-600">Critical Issues</span>
              </div>
              <p className={clsx(
                "text-xl font-bold",
                conflicts.conflict_count > 0 ? "text-red-600" : "text-gray-900"
              )}>
                {conflicts.conflict_count}
              </p>
            </div>
            
            <div className={clsx(
              "rounded-xl p-4 border",
              conflicts.warning_count > 0 ? "bg-amber-50 border-amber-200" : "bg-gray-50 border-gray-200"
            )}>
              <div className="flex items-center gap-2 mb-1">
                <AlertCircle className="w-5 h-5 text-amber-500" />
                <span className="text-sm text-gray-600">Warnings</span>
              </div>
              <p className={clsx(
                "text-xl font-bold",
                conflicts.warning_count > 0 ? "text-amber-600" : "text-gray-900"
              )}>
                {conflicts.warning_count}
              </p>
            </div>
          </div>

          {/* AI Summary */}
          <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl border border-purple-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Bot className="w-5 h-5 text-purple-600" />
              <span className="font-medium text-gray-900">AI Analysis</span>
            </div>
            <p className="text-gray-700">{conflicts.ai_summary}</p>
          </div>

          {/* Critical Conflicts */}
          {conflicts.conflicts.length > 0 && (
            <div className="bg-white rounded-xl border border-red-200 shadow-sm overflow-hidden">
              <div className="bg-red-50 px-4 py-3 border-b border-red-200">
                <h4 className="font-semibold text-red-800 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Critical Conflicts ({conflicts.conflicts.length})
                </h4>
              </div>
              <div className="divide-y divide-red-100">
                {conflicts.conflicts.map((conflict, index) => (
                  <div key={index} className="p-4 hover:bg-red-50 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                        <X className="w-4 h-4 text-red-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{conflict.message}</p>
                        <div className="flex flex-wrap gap-2 mt-2 text-sm">
                          {conflict.type && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded">
                              {conflict.type.replace(/_/g, ' ')}
                            </span>
                          )}
                          {conflict.vessel && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                              {conflict.vessel}
                            </span>
                          )}
                          {conflict.rank && (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                              {getRankLabel(conflict.rank)}
                            </span>
                          )}
                          {conflict.days_until_due !== undefined && (
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded">
                              {conflict.days_until_due} days until due
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Warnings */}
          {conflicts.warnings.length > 0 && (
            <div className="bg-white rounded-xl border border-amber-200 shadow-sm overflow-hidden">
              <div className="bg-amber-50 px-4 py-3 border-b border-amber-200">
                <h4 className="font-semibold text-amber-800 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Warnings ({conflicts.warnings.length})
                </h4>
              </div>
              <div className="divide-y divide-amber-100">
                {conflicts.warnings.map((warning, index) => (
                  <div key={index} className="p-4 hover:bg-amber-50 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <AlertCircle className="w-4 h-4 text-amber-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{warning.message}</p>
                        <div className="flex flex-wrap gap-2 mt-2 text-sm">
                          {warning.type && (
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded">
                              {warning.type.replace(/_/g, ' ')}
                            </span>
                          )}
                          {warning.vessel && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                              {warning.vessel}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No Issues */}
          {conflicts.is_healthy && conflicts.conflicts.length === 0 && conflicts.warnings.length === 0 && (
            <div className="text-center py-12 bg-green-50 rounded-xl border border-green-200">
              <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">All Clear!</h3>
              <p className="text-gray-600">No conflicts or issues detected in your relief plans.</p>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}

// ========================
// AI Analytics Panel
// ========================

function AnalyticsPanel() {
  const [analytics, setAnalytics] = useState<{
    period: { start: string; end: string; days: number };
    overview: {
      total_plans: number;
      confirmed_plans: number;
      completion_rate: number;
      avg_days_to_assignment: number;
    };
    monthly_trend: Array<{ month?: string; total: number; confirmed: number }>;
    by_rank: Array<{ rank: string; total: number; assignment_rate: number }>;
    top_vessels: Array<{ vessel: string; count: number }>;
    ai_insights: string[];
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [daysRange, setDaysRange] = useState(90);

  const loadAnalytics = async () => {
    setIsLoading(true);
    try {
      const result = await crewApi.getReliefPlanAnalytics(daysRange);
      setAnalytics(result);
    } catch (error) {
      toast.error('Failed to load analytics');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAnalytics();
  }, [daysRange]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Relief Plan Analytics</h3>
              <p className="text-sm text-gray-500">Comprehensive insights and trends</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={daysRange}
              onChange={(e) => setDaysRange(Number(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
              <option value={90}>Last 90 days</option>
              <option value={180}>Last 180 days</option>
            </select>
            <button
              onClick={loadAnalytics}
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Refresh
            </button>
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
        </div>
      )}

      {analytics && !isLoading && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <FileText className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-500">Total Plans</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{analytics.overview.total_plans}</p>
            </div>
            
            <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span className="text-sm text-gray-500">Confirmed</span>
              </div>
              <p className="text-2xl font-bold text-green-600">{analytics.overview.confirmed_plans}</p>
            </div>
            
            <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-blue-500" />
                <span className="text-sm text-gray-500">Completion Rate</span>
              </div>
              <p className="text-2xl font-bold text-blue-600">{analytics.overview.completion_rate}%</p>
            </div>
            
            <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-4 h-4 text-amber-500" />
                <span className="text-sm text-gray-500">Avg Days to Assign</span>
              </div>
              <p className="text-2xl font-bold text-amber-600">{analytics.overview.avg_days_to_assignment}</p>
            </div>
          </div>

          {/* AI Insights */}
          <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl border border-purple-200 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-purple-600" />
              <span className="font-medium text-gray-900">AI Insights</span>
            </div>
            <div className="space-y-2">
              {analytics.ai_insights.map((insight, i) => (
                <p key={i} className="text-sm text-gray-700 flex items-start gap-2">
                  <span className="text-purple-500">•</span>
                  {insight}
                </p>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* By Rank */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-600" />
                  Plans by Rank
                </h4>
              </div>
              <div className="p-4 space-y-3">
                {analytics.by_rank.map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-gray-700">{getRankLabel(item.rank)}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500">{item.total} plans</span>
                      <span className={clsx(
                        "text-sm px-2 py-0.5 rounded",
                        item.assignment_rate >= 80 ? "bg-green-100 text-green-700" :
                        item.assignment_rate >= 50 ? "bg-amber-100 text-amber-700" :
                        "bg-red-100 text-red-700"
                      )}>
                        {item.assignment_rate}% assigned
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Vessels */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Ship className="w-4 h-4 text-blue-600" />
                  Top Vessels by Activity
                </h4>
              </div>
              <div className="p-4 space-y-3">
                {analytics.top_vessels.slice(0, 5).map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-gray-700">{item.vessel}</span>
                    <span className="text-sm font-medium text-blue-600">{item.count} reliefs</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}

// ========================
// AI Auto-Generate Modal Component
// ========================

interface AutoGenerateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function AutoGenerateModal({ isOpen, onClose, onSuccess }: AutoGenerateModalProps) {
  const [daysAhead, setDaysAhead] = useState(30);
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    created_count: number;
    existing_count: number;
    created_plans: Array<{
      plan_id: number;
      vessel: string;
      rank: string;
      outgoing_crew: string;
      due_date?: string;
      suggested_replacement?: {
        cid: string;
        name: string;
        match_score: number;
      };
    }>;
    ai_summary: string;
  } | null>(null);

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      const res = await crewApi.aiAutoGenerateReliefPlans({
        days_ahead: daysAhead,
        include_existing: false,
      });
      setResult(res);
      toast.success(res.ai_summary);
      onSuccess();
    } catch (error) {
      toast.error('Failed to auto-generate relief plans');
      console.error(error);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="AI Auto-Generate Relief Plans" size="lg">
      <div className="space-y-6">
        {!result ? (
          <>
            <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
              <div className="flex items-start gap-3">
                <Wand2 className="w-6 h-6 text-purple-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-purple-900">Automatic Relief Plan Generation</h4>
                  <p className="text-sm text-purple-700 mt-1">
                    AI will analyze active crew due dates and automatically create relief plans 
                    for crew members approaching sign-off. Each plan will include an AI-suggested 
                    replacement based on experience and availability.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Days Ahead to Check
              </label>
              <select
                value={daysAhead}
                onChange={(e) => setDaysAhead(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
                <option value={60}>60 days</option>
                <option value={90}>90 days</option>
              </select>
              <p className="text-sm text-gray-500 mt-1">
                Creates relief plans for crew with due dates within this period
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerate}
                disabled={isGenerating}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
              >
                {isGenerating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Wand2 className="w-4 h-4" />
                )}
                Generate Relief Plans
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="bg-green-50 rounded-xl p-4 border border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="font-medium text-green-900">Generation Complete</span>
              </div>
              <p className="text-green-700">{result.ai_summary}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-green-600">{result.created_count}</p>
                <p className="text-sm text-gray-500">Plans Created</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-amber-600">{result.existing_count}</p>
                <p className="text-sm text-gray-500">Already Existed</p>
              </div>
            </div>

            {result.created_plans.length > 0 && (
              <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-gray-600">Vessel</th>
                      <th className="px-3 py-2 text-left text-gray-600">Rank</th>
                      <th className="px-3 py-2 text-left text-gray-600">Outgoing</th>
                      <th className="px-3 py-2 text-left text-gray-600">Suggested</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {result.created_plans.map((plan) => (
                      <tr key={plan.plan_id} className="hover:bg-gray-50">
                        <td className="px-3 py-2">{plan.vessel}</td>
                        <td className="px-3 py-2">{getRankLabel(plan.rank)}</td>
                        <td className="px-3 py-2">{plan.outgoing_crew}</td>
                        <td className="px-3 py-2">
                          {plan.suggested_replacement ? (
                            <span className="text-green-600">
                              {plan.suggested_replacement.name} ({plan.suggested_replacement.match_score}%)
                            </span>
                          ) : (
                            <span className="text-gray-400">None</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex justify-end pt-4 border-t">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// ========================
// Main Page Component
// ========================

export default function ReliefPlansPage() {
  // State
  const [reliefPlans, setReliefPlans] = useState<ReliefPlan[]>([]);
  const [vessels, setVessels] = useState<Vessel[]>([]);
  const [statistics, setStatistics] = useState<ReliefStatistics | null>(null);
  const [schedule, setSchedule] = useState<AIReliefScheduleResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [isLoadingSchedule, setIsLoadingSchedule] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalPlans, setTotalPlans] = useState(0);
  
  // View & Filters
  const [activeTab, setActiveTab] = useState<ViewTab>('plans');
  const [searchQuery, setSearchQuery] = useState('');
  const [rankFilter, setRankFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Modals
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isSuggestModalOpen, setIsSuggestModalOpen] = useState(false);
  const [isAutoGenerateModalOpen, setIsAutoGenerateModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<ReliefPlan | null>(null);
  
  // Loading states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);

  // Load data
  const loadReliefPlans = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await crewApi.getReliefPlans({
        rank: rankFilter || undefined,
        status: statusFilter || undefined,
        limit: 200,
      });
      setReliefPlans(result.plans);
      setTotalPlans(result.total);
    } catch (err) {
      setError('Failed to load relief plans');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [rankFilter, statusFilter]);

  const loadStatistics = async () => {
    setIsLoadingStats(true);
    try {
      const stats = await crewApi.getReliefStatistics();
      setStatistics(stats);
    } catch (err) {
      console.error('Failed to load statistics', err);
    } finally {
      setIsLoadingStats(false);
    }
  };

  const loadSchedule = async () => {
    setIsLoadingSchedule(true);
    try {
      const scheduleData = await crewApi.aiGetReliefSchedule({ days_ahead: 90 });
      setSchedule(scheduleData);
    } catch (err) {
      console.error('Failed to load schedule', err);
    } finally {
      setIsLoadingSchedule(false);
    }
  };

  const loadVessels = async () => {
    try {
      const result = await crewApi.getVessels();
      setVessels(result.vessels || []);
    } catch (err) {
      console.error('Failed to load vessels', err);
    }
  };

  useEffect(() => {
    loadReliefPlans();
    loadStatistics();
    loadVessels();
  }, [loadReliefPlans]);

  useEffect(() => {
    if (activeTab === 'schedule' && !schedule) {
      loadSchedule();
    }
  }, [activeTab]);

  // Filter plans by search
  const filteredPlans = reliefPlans.filter(plan => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      plan.vessel_name?.toLowerCase().includes(query) ||
      plan.outgoing_name?.toLowerCase().includes(query) ||
      plan.incoming_name?.toLowerCase().includes(query) ||
      plan.outgoing_cid?.toLowerCase().includes(query) ||
      plan.incoming_cid?.toLowerCase().includes(query)
    );
  });

  // CRUD Handlers
  const handleCreate = async (data: ReliefPlanFormData) => {
    setIsSubmitting(true);
    try {
      await crewApi.createReliefPlan(data);
      toast.success('Relief plan created successfully');
      setIsCreateModalOpen(false);
      loadReliefPlans();
      loadStatistics();
    } catch (err) {
      toast.error('Failed to create relief plan');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async (data: ReliefPlanFormData) => {
    if (!selectedPlan) return;
    setIsSubmitting(true);
    try {
      await crewApi.updateReliefPlan(selectedPlan.id, data);
      toast.success('Relief plan updated successfully');
      setIsEditModalOpen(false);
      setSelectedPlan(null);
      loadReliefPlans();
      loadStatistics();
    } catch (err) {
      toast.error('Failed to update relief plan');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPlan) return;
    setIsSubmitting(true);
    try {
      await crewApi.deleteReliefPlan(selectedPlan.id);
      toast.success('Relief plan deleted successfully');
      setIsDeleteModalOpen(false);
      setSelectedPlan(null);
      loadReliefPlans();
      loadStatistics();
    } catch (err) {
      toast.error('Failed to delete relief plan');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAssignCrew = async (crewCid: string) => {
    if (!selectedPlan) return;
    setIsAssigning(true);
    try {
      await crewApi.assignCrewToRelief(selectedPlan.id, crewCid);
      toast.success('Crew assigned successfully');
      setIsSuggestModalOpen(false);
      setSelectedPlan(null);
      loadReliefPlans();
      loadStatistics();
    } catch (err) {
      toast.error('Failed to assign crew');
      console.error(err);
    } finally {
      setIsAssigning(false);
    }
  };

  const handleConfirm = async (plan: ReliefPlan) => {
    try {
      await crewApi.confirmReliefPlan(plan.id);
      toast.success('Relief plan confirmed');
      loadReliefPlans();
      loadStatistics();
    } catch (err) {
      toast.error('Failed to confirm relief plan');
      console.error(err);
    }
  };

  const handleAutoAssign = async (plan: ReliefPlan) => {
    try {
      const result = await crewApi.autoAssignReliefCrew(plan.id);
      toast.success(result.ai_reasoning);
      loadReliefPlans();
      loadStatistics();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to auto-assign crew');
      console.error(err);
    }
  };

  const handleCreatePlanFromSchedule = async (crewCid: string) => {
    try {
      const result = await crewApi.aiCreateReliefFromSchedule({
        crew_cid: crewCid,
        auto_suggest_replacement: true,
      });
      toast.success(result.message || 'Relief plan created');
      loadReliefPlans();
      loadStatistics();
      loadSchedule();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to create relief plan');
      console.error(err);
    }
  };

  // Determine plan status for display
  const getPlanStatus = (plan: ReliefPlan): 'pending' | 'confirmed' | 'overdue' | 'cancelled' => {
    if (plan.status === 'confirmed') return 'confirmed';
    if (plan.status === 'cancelled') return 'cancelled';
    if (isOverdue(plan.due_date)) return 'overdue';
    return 'pending';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
              <CalendarClock className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Relief Plans</h1>
              <p className="text-gray-500">Manage crew change schedules with AI assistance</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsAutoGenerateModalOpen(true)}
              className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 shadow-sm transition-colors"
            >
              <Wand2 className="w-5 h-5 mr-2" />
              AI Auto-Generate
            </button>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-sm transition-colors"
            >
              <Plus className="w-5 h-5 mr-2" />
              New Relief Plan
            </button>
          </div>
        </motion.div>

        {/* Statistics Dashboard */}
        <StatisticsDashboard stats={statistics} isLoading={isLoadingStats} />

        {/* Tab Navigation */}
        <div className="flex items-center gap-1 mb-6 bg-white rounded-lg border border-gray-200 p-1 w-fit overflow-x-auto">
          <button
            onClick={() => setActiveTab('plans')}
            className={clsx(
              "px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap",
              activeTab === 'plans' 
                ? "bg-blue-100 text-blue-700" 
                : "text-gray-600 hover:bg-gray-100"
            )}
          >
            <ListTodo className="w-4 h-4" />
            Relief Plans
          </button>
          <button
            onClick={() => setActiveTab('schedule')}
            className={clsx(
              "px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap",
              activeTab === 'schedule' 
                ? "bg-blue-100 text-blue-700" 
                : "text-gray-600 hover:bg-gray-100"
            )}
          >
            <CalendarDays className="w-4 h-4" />
            Relief Schedule
          </button>
          <button
            onClick={() => setActiveTab('conflicts')}
            className={clsx(
              "px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap",
              activeTab === 'conflicts' 
                ? "bg-red-100 text-red-700" 
                : "text-gray-600 hover:bg-gray-100"
            )}
          >
            <AlertTriangle className="w-4 h-4" />
            Conflict Detection
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={clsx(
              "px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap",
              activeTab === 'analytics' 
                ? "bg-blue-100 text-blue-700" 
                : "text-gray-600 hover:bg-gray-100"
            )}
          >
            <BarChart3 className="w-4 h-4" />
            Analytics
          </button>
          <button
            onClick={() => setActiveTab('ai-assistant')}
            className={clsx(
              "px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap",
              activeTab === 'ai-assistant' 
                ? "bg-purple-100 text-purple-700" 
                : "text-gray-600 hover:bg-gray-100"
            )}
          >
            <Bot className="w-4 h-4" />
            AI Assistant
          </button>
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'plans' && (
            <motion.div
              key="plans"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              {/* Filters */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 shadow-sm">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search by vessel, crew name, or CID..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <select
                    value={rankFilter}
                    onChange={(e) => setRankFilter(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {rankOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {statusOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <button
                    onClick={loadReliefPlans}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Refresh
                  </button>
                </div>
              </div>

              {/* Loading State */}
              {isLoading && (
                <div className="text-center py-12">
                  <Loader2 className="w-10 h-10 text-blue-600 animate-spin mx-auto mb-4" />
                  <p className="text-gray-500">Loading relief plans...</p>
                </div>
              )}

              {/* Error State */}
              {error && !isLoading && (
                <div className="bg-white rounded-xl border border-red-200 p-8 text-center">
                  <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to Load Relief Plans</h3>
                  <p className="text-gray-500 mb-4">{error}</p>
                  <button
                    onClick={loadReliefPlans}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Try Again
                  </button>
                </div>
              )}

              {/* Relief Plans Table */}
              {!isLoading && !error && (
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  {filteredPlans.length === 0 ? (
                    <div className="text-center py-12">
                      <CalendarClock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No Relief Plans Found</h3>
                      <p className="text-gray-500 mb-4">
                        {searchQuery || rankFilter || statusFilter 
                          ? 'Try adjusting your filters' 
                          : 'Create your first relief plan to get started'}
                      </p>
                      <button
                        onClick={() => setIsCreateModalOpen(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        <Plus className="w-4 h-4 inline mr-2" />
                        New Relief Plan
                      </button>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Vessel / Position</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Outgoing Crew</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Incoming Crew</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Due Date</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {filteredPlans.slice(0, 100).map((plan, index) => {
                            const displayStatus = getPlanStatus(plan);
                            return (
                              <motion.tr
                                key={plan.id}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: index * 0.01 }}
                                className="hover:bg-gray-50 transition-colors"
                              >
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                                      <Ship className="w-5 h-5 text-blue-600" />
                                    </div>
                                    <div>
                                      <p className="font-medium text-gray-900">{plan.vessel_name || 'N/A'}</p>
                                      <p className="text-sm text-gray-500">{getRankLabel(plan.rank || '')}</p>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center">
                                      <User className="w-4 h-4 text-red-500" />
                                    </div>
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">{plan.outgoing_name || 'Not specified'}</p>
                                      {plan.outgoing_cid && (
                                        <p className="text-xs text-gray-500">CID: {plan.outgoing_cid}</p>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center">
                                      <User className="w-4 h-4 text-green-500" />
                                    </div>
                                    <div>
                                      {plan.incoming_name ? (
                                        <>
                                          <p className="text-sm font-medium text-gray-900">{plan.incoming_name}</p>
                                          {plan.incoming_cid && (
                                            <p className="text-xs text-gray-500">CID: {plan.incoming_cid}</p>
                                          )}
                                        </>
                                      ) : (
                                        <button
                                          onClick={() => { setSelectedPlan(plan); setIsSuggestModalOpen(true); }}
                                          className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1"
                                        >
                                          <Wand2 className="w-4 h-4" />
                                          Find Crew with AI
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-1.5 text-sm text-gray-600">
                                    <Calendar className="w-4 h-4" />
                                    <span className={clsx(displayStatus === 'overdue' && "text-red-600 font-medium")}>
                                      {formatDate(plan.due_date)}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  <span className={clsx(
                                    'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border',
                                    statusStyles[displayStatus]
                                  )}>
                                    {displayStatus === 'confirmed' && <CheckCircle className="w-3 h-3 mr-1" />}
                                    {displayStatus === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                                    {displayStatus === 'overdue' && <AlertTriangle className="w-3 h-3 mr-1" />}
                                    {displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)}
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex justify-end gap-1">
                                    <button
                                      onClick={() => { setSelectedPlan(plan); setIsViewModalOpen(true); }}
                                      className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                      title="View Details"
                                    >
                                      <Eye className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => { setSelectedPlan(plan); setIsEditModalOpen(true); }}
                                      className="p-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                                      title="Edit"
                                    >
                                      <Edit2 className="w-4 h-4" />
                                    </button>
                                    {!plan.incoming_cid && (
                                      <button
                                        onClick={() => handleAutoAssign(plan)}
                                        className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                                        title="AI Auto-Assign"
                                      >
                                        <Wand2 className="w-4 h-4" />
                                      </button>
                                    )}
                                    {plan.incoming_cid && plan.status !== 'confirmed' && (
                                      <button
                                        onClick={() => handleConfirm(plan)}
                                        className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                        title="Confirm"
                                      >
                                        <Check className="w-4 h-4" />
                                      </button>
                                    )}
                                    <button
                                      onClick={() => { setSelectedPlan(plan); setIsDeleteModalOpen(true); }}
                                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                      title="Delete"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </td>
                              </motion.tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Pagination Info */}
              {!isLoading && !error && filteredPlans.length > 0 && (
                <div className="mt-4 text-sm text-gray-500 text-center">
                  Showing {Math.min(filteredPlans.length, 100)} of {totalPlans} relief plans
                </div>
              )}
            </motion.div>
          )}

          {activeTab === 'schedule' && (
            <motion.div
              key="schedule"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <ReliefScheduleView 
                schedule={schedule}
                isLoading={isLoadingSchedule}
                onRefresh={loadSchedule}
                onCreatePlan={handleCreatePlanFromSchedule}
              />
            </motion.div>
          )}

          {activeTab === 'conflicts' && (
            <motion.div
              key="conflicts"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <ConflictsPanel onRefreshPlans={() => { loadReliefPlans(); loadStatistics(); }} />
            </motion.div>
          )}

          {activeTab === 'analytics' && (
            <motion.div
              key="analytics"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <AnalyticsPanel />
            </motion.div>
          )}

          {activeTab === 'ai-assistant' && (
            <motion.div
              key="ai-assistant"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <AIAssistantPanel onRefreshPlans={() => { loadReliefPlans(); loadStatistics(); }} />
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Create Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Create New Relief Plan"
        size="lg"
      >
        <ReliefPlanForm
          onSubmit={handleCreate}
          onCancel={() => setIsCreateModalOpen(false)}
          isLoading={isSubmitting}
          vessels={vessels}
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => { setIsEditModalOpen(false); setSelectedPlan(null); }}
        title="Edit Relief Plan"
        size="lg"
      >
        {selectedPlan && (
          <ReliefPlanForm
            initialData={{
              vessel_name: selectedPlan.vessel_name,
              rank: selectedPlan.rank,
              outgoing_cid: selectedPlan.outgoing_cid,
              outgoing_name: selectedPlan.outgoing_name,
              incoming_cid: selectedPlan.incoming_cid,
              incoming_name: selectedPlan.incoming_name,
              due_date: selectedPlan.due_date,
              status: selectedPlan.status,
              notes: selectedPlan.notes,
            }}
            onSubmit={handleUpdate}
            onCancel={() => { setIsEditModalOpen(false); setSelectedPlan(null); }}
            isLoading={isSubmitting}
            vessels={vessels}
          />
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setSelectedPlan(null); }}
        title="Confirm Delete"
        size="sm"
      >
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <Trash2 className="w-8 h-8 text-red-600" />
          </div>
          <p className="text-gray-700 mb-2">
            Are you sure you want to delete this relief plan?
          </p>
          <p className="text-sm text-gray-500 mb-6">
            {selectedPlan?.rank} position on <strong>{selectedPlan?.vessel_name}</strong>
          </p>
          <div className="flex justify-center gap-3">
            <button
              onClick={() => { setIsDeleteModalOpen(false); setSelectedPlan(null); }}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={isSubmitting}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
            >
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              Delete
            </button>
          </div>
        </div>
      </Modal>

      {/* View Details Modal */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => { setIsViewModalOpen(false); setSelectedPlan(null); }}
        title="Relief Plan Details"
        size="lg"
      >
        {selectedPlan && (
          <div className="space-y-6">
            <div className="flex items-center gap-4 pb-4 border-b">
              <div className="w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center">
                <Ship className="w-7 h-7 text-blue-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">{selectedPlan.vessel_name}</h3>
                <p className="text-gray-500">{getRankLabel(selectedPlan.rank || '')} Position</p>
              </div>
              <div className="ml-auto">
                <span className={clsx(
                  'inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium border',
                  statusStyles[getPlanStatus(selectedPlan)]
                )}>
                  {getPlanStatus(selectedPlan).charAt(0).toUpperCase() + getPlanStatus(selectedPlan).slice(1)}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-red-50 rounded-xl p-4 border border-red-100">
                <h4 className="text-sm font-medium text-red-700 mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Outgoing Crew (Signing Off)
                </h4>
                <p className="font-semibold text-gray-900">{selectedPlan.outgoing_name || 'Not specified'}</p>
                {selectedPlan.outgoing_cid && (
                  <p className="text-sm text-gray-500">CID: {selectedPlan.outgoing_cid}</p>
                )}
              </div>
              <div className="bg-green-50 rounded-xl p-4 border border-green-100">
                <h4 className="text-sm font-medium text-green-700 mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Incoming Crew (Signing On)
                </h4>
                {selectedPlan.incoming_name ? (
                  <>
                    <p className="font-semibold text-gray-900">{selectedPlan.incoming_name}</p>
                    {selectedPlan.incoming_cid && (
                      <p className="text-sm text-gray-500">CID: {selectedPlan.incoming_cid}</p>
                    )}
                  </>
                ) : (
                  <p className="text-amber-600 font-medium">Pending Assignment</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-1">Due Date</p>
                <p className="font-semibold text-gray-900">{formatDate(selectedPlan.due_date)}</p>
              </div>
              {selectedPlan.relief_date && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500 mb-1">Relief Date</p>
                  <p className="font-semibold text-gray-900">{formatDate(selectedPlan.relief_date)}</p>
                </div>
              )}
            </div>

            {selectedPlan.notes && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Notes</h4>
                <p className="text-gray-600 bg-gray-50 rounded-lg p-3">{selectedPlan.notes}</p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                onClick={() => { setIsViewModalOpen(false); setIsEditModalOpen(true); }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2"
              >
                <Edit2 className="w-4 h-4" />
                Edit
              </button>
              {!selectedPlan.incoming_cid && (
                <button
                  onClick={() => { setIsViewModalOpen(false); setIsSuggestModalOpen(true); }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                >
                  <Sparkles className="w-4 h-4" />
                  Find Crew with AI
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* AI Suggestion Modal */}
      <Modal
        isOpen={isSuggestModalOpen}
        onClose={() => { setIsSuggestModalOpen(false); setSelectedPlan(null); }}
        title="AI Crew Suggestions"
        size="lg"
      >
        {selectedPlan && (
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-3">
                <Ship className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-medium text-gray-900">{selectedPlan.vessel_name}</p>
                  <p className="text-sm text-gray-500">
                    {getRankLabel(selectedPlan.rank || '')} • Due: {formatDate(selectedPlan.due_date)}
                  </p>
                </div>
              </div>
            </div>
            
            <AISuggestionPanel
              planId={selectedPlan.id}
              rank={selectedPlan.rank || ''}
              vesselName={selectedPlan.vessel_name || ''}
              onAssign={handleAssignCrew}
              isAssigning={isAssigning}
            />
          </div>
        )}
      </Modal>

      {/* AI Auto-Generate Modal */}
      <AutoGenerateModal
        isOpen={isAutoGenerateModalOpen}
        onClose={() => setIsAutoGenerateModalOpen(false)}
        onSuccess={() => {
          loadReliefPlans();
          loadStatistics();
        }}
      />
    </div>
  );
}
